import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MobileAwareComponent} from '../../common/mobile-aware/mobile-aware.component';
import {BreakpointObserver} from '@angular/cdk/layout';
import {Contest} from '../../model/contest/Contest';
import {ContestService} from '../../service/contest.service';
import {
  ContestDetailsData,
  ContestDetailsModalComponent
} from '../contest-details-modal/contest-details-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {LocalStorageService} from 'ngx-webstorage';
import {ListItemDetailsModalComponent} from '../../common/list-item-details-modal/list-item-details-modal.component';
import {
  DeleteConfirmationData,
  DeleteConfirmationModalComponent,
  DeleteContext
} from '../../common/modal/delete-confirmation-modal/delete-confirmation-modal.component';
import {localizePaymentStatus, mobilePaymentStatusIcon, PaymentStatus} from '../../model/Payment';
import {
  ConfirmationContext,
  ConfirmationData,
  ConfirmationModalComponent
} from '../../common/modal/confirmation-modal/confirmation-modal.component';
import {localizeRole, Role} from '../../model/auth/Role';
import {Router} from '@angular/router';
import {UrlHelper} from '../../helper/url.helper';
import {
  ContestCertificateModalComponent,
  ContestCertificateModalData
} from '../contest-certificate-modal/contest-certificate-modal.component';
import {ContestPreviewModalComponent} from '../contest-preview-modal/contest-preview-modal.component';
import {ContestDirectUrlModalComponent} from '../contest-direct-url-modal/contest-direct-url-modal.component';
import {
  ListItemDetailsActionInvoke,
  ListItemDetailsData
} from '../../common/list-item-details/list-item-details.component';
import {
  SelectTeacherModalComponent,
  TeacherSelectData
} from '../../teacher/select-teacher-modal/select-teacher-modal.component';
import {User} from '../../model/auth/User';
import {
  ContestTaskCloneSelectModalComponent,
  ContestTaskCopyDetailsData
} from '../contest-task-clone-select-modal/contest-task-clone-select-modal.component';
import {
  ContestsTeacherInfoModalComponent,
  ContestTeacherInfoDetailsData
} from '../contest-teacher-info-modal/contests-teacher-info-modal.component';

interface ContestMobileDetailsData {
  contest: Contest;
}

@Component({
  selector: 'contests-list',
  templateUrl: './contests-list.component.html',
  styleUrls: ['./contests-list.component.scss']
})
export class ContestsListComponent extends MobileAwareComponent {

  readonly paymentStatuses = PaymentStatus;

  @Input() competitionId!: string;
  @Input() contests!: Contest[];

  @Input() userId?: string;
  @Input() userRole?: Role;

  @Input() userRegion?: string;
  @Input() userSchool?: string;

  @Input() contestsOpenForEnrol?: Contest[] | null;

  @Input() showEnrolEnd = false;
  @Input() showPayment = false;
  @Input() showTeacher = false;
  @Input() showTeacherDetails = false;
  @Input() editAllowed = true;
  @Input() deleteAllowed = true;
  @Input() cancelEnrolAllowed = false;

  @Input() detailsAction?: string;

  @Output() listUpdated = new EventEmitter<string>();
  @Output() detailsRequested = new EventEmitter<Contest>();
  @Output() handlePaymentRetry = new EventEmitter<Contest>();
  @Output() handlePaymentVerify = new EventEmitter<Contest>();

  constructor(private contestService: ContestService,
              private localStorageService: LocalStorageService,
              private dialog: MatDialog,
              private router: Router,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  isContestCancelEnrolAllowed(contest: Contest): boolean {
    return this.cancelEnrolAllowed && contest
      && (contest.paymentStatus == PaymentStatus.scheduled || contest.paymentStatus == PaymentStatus.failed);
  }

  isContestInProgress(contest: Contest): boolean {
    const today = new Date();

    return this.userRole == Role.Student
      && contest.startsAt != undefined
      && contest.endsAt != undefined
      && contest.startsAt < today
      && contest.endsAt > today;
  }

  isContestStarted(contest: Contest): boolean {
    return !contest.startsAt || contest.startsAt < new Date();
  }

  isContestFinishedForStudent(contest: Contest): boolean {
    return this.userRole == Role.Student && this.isContestFinished(contest);
  }

  isContestFinished(contest: Contest): boolean {
    return !!contest.endsAt && contest.endsAt < new Date();
  }

  isCertificateEditAllowed(contest: Contest): boolean {
    return !!contest.endsAt && contest.endsAt < new Date();
  }

  get teacherLabel(): string {
    return localizeRole(Role.Teacher);
  }

  showDetails(contest: Contest) {
    if (this.mobileView) {
      const data: ListItemDetailsData<ContestMobileDetailsData> = {
        details: [{
          name: $localize`:Contests List|Contests list item 'name' title@@contests-list-item-name-title:Name:`,
          value: contest.name
        }],
        actions: [],
        component: {
          contest: contest
        }
      };
      if (this.showEnrolEnd) {
        data.details.push({
          name: $localize`:Contests List|Contests list item 'enrol ends at' title@@contests-list-item-enrol-ends-at-title:Enrol Ends At:`,
          value: contest.enrolEndsAt,
        });
      }
      data.details.push({
        name: $localize`:Contests List|Contests list item 'starts at' title@@contests-list-item-starts-at-title:Starts At:`,
        value: contest.startsAt,
      });
      data.details.push({
        name: $localize`:Contests List|Contests list item 'ends at' title@@contests-list-item-ends-at-title:Ends At:`,
        value: contest.endsAt,
      });
      if (this.showPayment) {
        data.details.push({
          name: $localize`:Contests List|Contests list item 'payment' title@@contests-list-item-payment-title:Payment:`,
          value: contest.paymentStatus ? this.localizeStatus(contest.paymentStatus) : '–',
        });
      }

      if (this.editAllowed && data.actions) {
        if (!this.isContestStarted(contest)) {
          data.actions.push({
            code: 'edit',
            'name': $localize`:General|Edit action label@@general-edit-action-label:Edit`
          });
        }
        if (this.isContestStarted(contest) || this.isContestFinished(contest)) {
          data.actions.push({
            code: 'show-details',
            'name': $localize`:General|Details action label@@general-details-action-label:Details`
          });
        }
        data.actions.push({
          code: 'tasks',
          'name': $localize`:General|Tasks action label@@general-tasks-action-label:Tasks`
        });

        if (!this.isContestFinished(contest)) {
          data.actions.push({
            code: 'directUrl',
            'name': $localize`:General|Direct URL action label@@general-direct-url-action-label:Direct URL`
          });
        }

        data.actions.push({
          code: 'preview',
          'name': $localize`:General|Preview action label@@general-preview-action-label:Preview`
        });

        if (!this.isCertificateEditAllowed(contest)) {
          data.actions.push({
            code: 'certificate',
            'name': $localize`:General|Certificate action label@@general-certificate-action-label:Certificate`
          });
        }
      }
      if (this.deleteAllowed && data.actions) {
        data.actions.push({
          code: 'delete',
          'name': $localize`:General|Delete action label@@general-delete-action-label:Delete`
        });
      }
      if (this.isContestCancelEnrolAllowed(contest) && data.actions) {
        data.actions.push({
          code: 'cancel',
          'name': $localize`:General|Cancel action label@@general-cancel-action-label:Cancel`
        });
      }
      if (this.isContestInProgress(contest) && contest.paymentStatus == PaymentStatus.successful && data.actions) {
        data.actions.push({
          code: 'participate',
          'name': $localize`:General|Participate action label@@general-participate-action-label:Participate`
        });
      }
      if (this.isContestFinishedForStudent(contest) && contest.paymentStatus == PaymentStatus.successful && data.actions) {
        data.actions.push({
          code: 'results',
          'name': $localize`:General|Results action label@@general-results-action-label:Results`
        });
      }

      if (this.detailsAction && data.actions) {
        data.actions.push({
          code: 'details',
          name: this.detailsAction
        });
      }

      this.dialog.open(ListItemDetailsModalComponent, {
        width: '600px',
        data: data
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
      }).afterClosed().subscribe(async (data: ListItemDetailsActionInvoke<ContestMobileDetailsData>) => {
        if (data && (this.editAllowed || this.detailsAction
          || (contest.paymentStatus == PaymentStatus.successful && (this.isContestInProgress(contest) || this.isContestFinishedForStudent(contest))))) {
          switch (data.action) {
            case 'edit': {
              if (data.component?.contest) {
                this.editContest(data.component?.contest);
              }
              break;
            }
            case 'show-details': {
              if (data.component?.contest) {
                this.showContestDetails(data.component?.contest);
              }
              break;
            }
            case 'tasks': {
              if (data.component?.contest) {
                await this.editContestTasks(data.component?.contest);
              }
              break;
            }
            case 'directUrl': {
              if (data.component?.contest) {
                this.getExamDirectUrl(data.component?.contest);
              }
              break;
            }
            case 'preview': {
              if (data.component?.contest) {
                this.previewContestTasks(data.component?.contest);
              }
              break;
            }
            case 'certificate': {
              if (data.component?.contest) {
                this.editContestCertificate(data.component?.contest);
              }
              break;
            }
            case 'delete': {
              if (data.component?.contest) {
                this.deleteContest(data.component?.contest);
              }
              break;
            }
            case 'details': {
              if (data.component?.contest) {
                this.detailsRequested.emit(data.component.contest);
              }
              break;
            }
            case 'participate': {
              if (data.component?.contest) {
                await this.goToExam(data.component.contest);
              }
              break;
            }
            case 'results': {
              if (data.component?.contest) {
                await this.router.navigate([this.getExamCardUrl(data.component.contest)]);
              }
              break;
            }
          }
        }
      });
    } else {
      this.detailsRequested.emit(contest);
    }
  }

  editContest(contest: Contest) {
    const data: ContestDetailsData = {
      competitionId: this.competitionId,
      contest: contest,
      readOnly: false,
    };
    this.dialog.open(ContestDetailsModalComponent, {
      width: '600px',
      data
    }).afterClosed().subscribe((data: ContestDetailsData) => {
      if (data && data.competitionId) {
        this.listUpdated.emit(data.competitionId);
      }
    });
  }

  showContestDetails(contest: Contest) {
    const data: ContestDetailsData = {
      competitionId: this.competitionId,
      contest: contest,
      readOnly: true,
    };
    this.dialog.open(ContestDetailsModalComponent, {
      width: '600px',
      data
    });
  }

  getExamDirectUrl(contest: Contest) {
    this.dialog.open(ContestDirectUrlModalComponent, {
      width: '600px',
      data: {
        competitionId: this.competitionId,
        contest: contest
      }
    });
  }

  previewContestTasks(contest: Contest) {
    this.dialog.open(ContestPreviewModalComponent, {
      width: '600px',
      data: {
        competitionId: this.competitionId,
        contest: contest
      }
    });
  }

  editContestCertificate(contest: Contest) {
    const data: ContestCertificateModalData = {
      competitionId: this.competitionId,
      contestId: contest.id
    };
    this.dialog.open(ContestCertificateModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe((data: ContestDetailsData) => {
      if (data && data.competitionId) {
        this.listUpdated.emit(data.competitionId);
      }
    });
  }

  deleteContest(contest: Contest) {
    const data: DeleteConfirmationData = {
      title: $localize`:Contests Details|Contests details delete item confirmation title@@contests-details-delete-item-confirmation-title:Contest Delete`,
      itemType: $localize`:Contests Details|Contests details delete item type@@contests-details-delete-item-type:Contest`,
      name: contest.name,
      actionDesc: $localize`:Contests Details|Contests details delete item prompt@@contests-details-delete-item-prompt:will be removed from the system and all assigned users`,
      archive: false
    };
    const dialogRef = this.dialog.open(DeleteConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.deleteConfirmed.subscribe((context: DeleteContext) => {
      this.contestService.delete(contest.id).subscribe({
        next: () => {
          context.modal.deleteHandled();

          this.listUpdated.emit(this.competitionId || '');
        },
        error: error => {
          context.modal.deleteHandled($localize`:Notifications|Contest delete has failed message@@notifications-contest-delete-failed-error:Contest delete has failed`, error);
        }
      });
    });
  }

  cancelContestEnrol(contest: Contest) {
    const data: ConfirmationData = {
      title: $localize`:Contests Details|Cancel contests enrollment modal title@@contests-details-cancel-contests-enrollment-modal-title:Cancel Contest Enrollment`,
      content: $localize`:Contests Details|Cancel contests enrollment details@@contests-details-cancel-contests-enrollment-details:Do you want to cancel your enrollment for this contest?`
    };
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.actionConfirmed.subscribe((context: ConfirmationContext) => {
      if (this.userRole && this.userId) {
        this.contestService.deleteUser(this.competitionId, contest.id, this.userRole, this.userId).subscribe({
          next: () => {
            const index = this.contests.findIndex(c => c.id == contest.id);
            if (index >= 0) this.contests.splice(index, 1);
            context.modal.proceedHandled();

            this.listUpdated.emit(this.competitionId || '');
          },
          error: error => {
            context.modal.proceedHandled($localize`:Notifications|Contest enrollment delete has failed message@@notifications-contest-enrollment-delete-failed-error:Contest enrollment delete has failed`, error);
          }
        });
      }
    });
  }

  showTeacherContestInfo(contest: Contest) {
    if (!this.userId) return;

    const data: ContestTeacherInfoDetailsData = {
      competitionId: this.competitionId,
      contestId: contest.id,
      userId: this.userId
    };

    this.dialog.open(ContestsTeacherInfoModalComponent, {
      width: '600px',
      data: data
    });
  }

  localizeStatus(paymentStatus: PaymentStatus) {
    return localizePaymentStatus(paymentStatus);
  }

  mobileStatusIcon(paymentStatus: PaymentStatus) {
    return mobilePaymentStatusIcon(paymentStatus);
  }

  retryAllowed(contest: Contest) {
    if (!this.contestsOpenForEnrol) return false;

    return this.contestsOpenForEnrol.findIndex(c => c.id == contest.id) >= 0;
  }

  retryClicked(contest: Contest) {
    this.handlePaymentRetry.emit(contest);
  }

  verifyClicked(contest: Contest) {
    this.handlePaymentVerify.emit(contest);
  }

  async editContestTasks(contest: Contest) {
    await this.router.navigate([UrlHelper.CONTESTS_DETAILS_PATH + contest.id]);
  }

  cloneContestTasks(contest: Contest) {
    if (!this.competitionId) return;

    const data: ContestTaskCopyDetailsData = {
      competitionId: this.competitionId,
      contest: contest,
    };

    this.dialog.open(ContestTaskCloneSelectModalComponent, {
      width: '600px',
      data: data
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
    }).afterClosed().subscribe(async (result?: ContestTaskCopyDetailsData) => {
      if (result?.contest) {
        await this.editContestTasks(contest);
      }
    });
  }

  async goToExam(contest: Contest) {
    await this.router.navigate([UrlHelper.CONTEST_EXAM_PATH + contest.id]);
  }

  getExamCardUrl(contest: Contest) {
    return `${UrlHelper.STATS_EXAM_CARD_PATH}${contest.id}/${this.userId}`;
  }

  assignTeacher(contest: Contest) {
    if (!this.userId || !this.userRegion || !this.userSchool) return;

    const data: TeacherSelectData = {
      competitionId: this.competitionId,
      contestId: contest.id,
      regionId: this.userRegion,
      schoolId: this.userSchool,
      userIds: [this.userId],
    };

    if (contest.teacher_id) {
      data.teacher = {
        teacher_id: contest.teacher_id,
        teacher_first_name: contest.teacher_first_name,
        teacher_last_name: contest.teacher_last_name,
      };
    }

    this.dialog.open(SelectTeacherModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe((users: User[]) => {
      if (users?.length) {
        const user = users[0];
        contest.teacher_id = user.teacher_id;
        contest.teacher_first_name = user.teacher_first_name;
        contest.teacher_last_name = user.teacher_last_name;
      }
    });
  }
}
